import React, { useState, useRef, useEffect } from 'react';
import { FaUpload, FaPlay, FaStop, FaMicrophone, FaTimes, FaCheck } from 'react-icons/fa';
import { voiceApiService } from '../../services/voiceApiService';
import useNotification from '../../hooks/useNotification';
import './VoiceUpload.css';

const { uploadVoiceFile, getVoiceByProverbId } = voiceApiService;

const VoiceUpload = ({ proverbId, language = 'chinese', onUploadSuccess, onClose }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [audioUrl, setAudioUrl] = useState(null);
  
  // New states for existing voice file
  const [existingVoice, setExistingVoice] = useState(null);
  const [loadingExisting, setLoadingExisting] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [playingExisting, setPlayingExisting] = useState(false);
  
  // Voice recording states
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [playingRecorded, setPlayingRecorded] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [recordingTimer, setRecordingTimer] = useState(null);
  
  const fileInputRef = useRef(null);
  const audioRef = useRef(null);
  const existingAudioRef = useRef(null);
  const recordedAudioRef = useRef(null);
  const { showNotification } = useNotification();

  // Function to stop all audio playback
  const stopAllAudio = () => {
    // Stop existing audio
    if (existingAudioRef.current && !existingAudioRef.current.paused) {
      existingAudioRef.current.pause();
      existingAudioRef.current.currentTime = 0;
      setPlayingExisting(false);
    }
    
    // Stop recorded audio
    if (recordedAudioRef.current && !recordedAudioRef.current.paused) {
      recordedAudioRef.current.pause();
      recordedAudioRef.current.currentTime = 0;
      setPlayingRecorded(false);
    }
    
    // Stop selected file audio
    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  // Cleanup audio URL on component unmount
  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      if (recordedUrl) {
        URL.revokeObjectURL(recordedUrl);
      }
      // Stop recording if component unmounts
      if (mediaRecorder && mediaRecorder.state === 'recording') {
        mediaRecorder.stop();
      }
      // Clear recording timer
      if (recordingTimer) {
        clearInterval(recordingTimer);
      }
    };
  }, [audioUrl, recordedUrl, mediaRecorder, recordingTimer]);

  // Load existing voice file on component mount
  useEffect(() => {
    const loadExistingVoice = async () => {
      try {
        setLoadingExisting(true);
        const voice = await getVoiceByProverbId(proverbId);
        if (voice) {
          setExistingVoice(voice);
          console.log('🎵 Existing voice loaded:', voice);
        }
      } catch (error) {
        console.error('Failed to load existing voice:', error);
        // Don't show error notification for missing voice - it's normal
      } finally {
        setLoadingExisting(false);
      }
    };

    if (proverbId) {
      loadExistingVoice();
    }
  }, [proverbId]);

  // Handle file selection
  const handleFileSelect = (files) => {
    const file = files[0];
    
    if (!file) return;
    
    // Validate file type
    if (!file.type.includes('audio/')) {
      showNotification('❌ Please select an audio file (MP3, WAV, etc.)', 'error');
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      showNotification('❌ File size must be less than 10MB', 'error');
      return;
    }

    // Clean up previous audio URL
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }

    // Create new audio URL
    const newAudioUrl = URL.createObjectURL(file);
    setSelectedFile(file);
    setAudioUrl(newAudioUrl);
    setIsPlaying(false);
    
    console.log('🎵 Audio file selected:', {
      name: file.name,
      size: file.size,
      type: file.type,
      url: newAudioUrl
    });
    
    showNotification(`✅ File selected: ${file.name}`, 'success');
  };

  // Voice Recording Functions
  const startRecording = async () => {
    try {
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      
      // Try to use the best available format for MP3 compatibility
      let mimeType = 'audio/webm';
      
      // Priority order: MP4 > MPEG > WebM with Opus > WebM
      if (MediaRecorder.isTypeSupported('audio/mp4;codecs=mp4a.40.2')) {
        mimeType = 'audio/mp4;codecs=mp4a.40.2'; // AAC codec in MP4 (most compatible)
      } else if (MediaRecorder.isTypeSupported('audio/mpeg')) {
        mimeType = 'audio/mpeg'; // Direct MP3 support (rare but best)
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4'; // MP4 container
      } else if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        mimeType = 'audio/webm;codecs=opus'; // WebM with Opus
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        mimeType = 'audio/webm'; // Basic WebM
      }
      
      console.log('🎤 Selected MIME type:', mimeType);
      console.log('🎤 Available formats check:', {
        'audio/mp4;codecs=mp4a.40.2': MediaRecorder.isTypeSupported('audio/mp4;codecs=mp4a.40.2'),
        'audio/mpeg': MediaRecorder.isTypeSupported('audio/mpeg'),
        'audio/mp4': MediaRecorder.isTypeSupported('audio/mp4'),
        'audio/webm;codecs=opus': MediaRecorder.isTypeSupported('audio/webm;codecs=opus'),
        'audio/webm': MediaRecorder.isTypeSupported('audio/webm')
      });
      
      // Create media recorder
      const recorder = new MediaRecorder(stream, { mimeType });
      
      const chunks = [];
      
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };
      
      recorder.onstop = () => {
        const blob = new Blob(chunks, { 
          type: recorder.mimeType || mimeType 
        });
        
        // Clean up previous recorded audio
        if (recordedUrl) {
          URL.revokeObjectURL(recordedUrl);
        }
        
        const url = URL.createObjectURL(blob);
        setRecordedBlob(blob);
        setRecordedUrl(url);
        setPlayingRecorded(false);
        
        // Stop all tracks to release microphone
        stream.getTracks().forEach(track => track.stop());
        
        console.log('🎤 Recording completed:', {
          size: blob.size,
          type: blob.type,
          duration: recordingDuration,
          mimeType: recorder.mimeType
        });
        
        showNotification(`✅ Recording completed! Duration: ${Math.floor(recordingDuration)}s`, 'success');
      };
      
      recorder.onerror = (event) => {
        console.error('Recording error:', event.error);
        showNotification(`❌ Recording error: ${event.error}`, 'error');
        setIsRecording(false);
        setRecordingDuration(0);
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };
      
      // Start recording
      recorder.start(1000); // Collect data every second
      setMediaRecorder(recorder);
      setIsRecording(true);
      setRecordingDuration(0);
      
      // Start timer
      const timer = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
      setRecordingTimer(timer);
      
      showNotification('🎤 Recording started...', 'info');
      
    } catch (error) {
      console.error('Failed to start recording:', error);
      if (error.name === 'NotAllowedError') {
        showNotification('❌ Microphone access denied. Please allow microphone access and try again.', 'error');
      } else if (error.name === 'NotFoundError') {
        showNotification('❌ No microphone found. Please check your microphone connection.', 'error');
      } else {
        showNotification(`❌ Failed to start recording: ${error.message}`, 'error');
      }
      setIsRecording(false);
    }
  };
  
  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state === 'recording') {
      mediaRecorder.stop();
    }
    
    if (recordingTimer) {
      clearInterval(recordingTimer);
      setRecordingTimer(null);
    }
    
    setIsRecording(false);
    setMediaRecorder(null);
  };
  
  const playRecordedAudio = async () => {
    if (!recordedAudioRef.current || !recordedUrl) {
      console.log('❌ Recorded audio playback failed: No audio element or URL');
      showNotification('❌ No recorded audio to play', 'error');
      return;
    }

    try {
      if (playingRecorded) {
        recordedAudioRef.current.pause();
        recordedAudioRef.current.currentTime = 0;
        setPlayingRecorded(false);
        console.log('⏸️ Recorded audio paused');
      } else {
        // Stop all other audio first
        stopAllAudio();
        
        console.log('▶️ Attempting to play recorded audio...');
        console.log('🎵 Recorded URL:', recordedUrl);
        
        // Make sure the audio source is set
        if (recordedAudioRef.current.src !== recordedUrl) {
          recordedAudioRef.current.src = recordedUrl;
        }
        
        // Wait for audio to load if needed
        if (recordedAudioRef.current.readyState === 0) {
          console.log('📥 Recorded audio not loaded, waiting...');
          await new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
              recordedAudioRef.current.removeEventListener('loadeddata', onLoadedData);
              recordedAudioRef.current.removeEventListener('error', onError);
              reject(new Error('Audio loading timeout'));
            }, 5000); // 5 second timeout
            
            const onLoadedData = () => {
              clearTimeout(timeout);
              recordedAudioRef.current.removeEventListener('loadeddata', onLoadedData);
              recordedAudioRef.current.removeEventListener('error', onError);
              resolve();
            };
            
            const onError = (e) => {
              clearTimeout(timeout);
              recordedAudioRef.current.removeEventListener('loadeddata', onLoadedData);
              recordedAudioRef.current.removeEventListener('error', onError);
              reject(new Error(`Audio loading error: ${e.target.error?.message || 'Unknown error'}`));
            };
            
            recordedAudioRef.current.addEventListener('loadeddata', onLoadedData);
            recordedAudioRef.current.addEventListener('error', onError);
            
            // Try to load the audio
            recordedAudioRef.current.load();
          });
        }
        
        await recordedAudioRef.current.play();
        setPlayingRecorded(true);
        console.log('✅ Recorded audio playing successfully');
      }
    } catch (error) {
      console.error('❌ Recorded audio playback error:', error);
      setPlayingRecorded(false);
      
      if (error.name === 'NotSupportedError') {
        showNotification('❌ Recorded audio format not supported by browser', 'error');
      } else if (error.name === 'NotAllowedError') {
        showNotification('❌ Audio playback blocked. Please allow audio in your browser', 'error');
      } else {
        showNotification(`❌ Failed to play recorded audio: ${error.message}`, 'error');
      }
    }
  };
  
  const clearRecording = () => {
    if (recordedUrl) {
      URL.revokeObjectURL(recordedUrl);
    }
    setRecordedBlob(null);
    setRecordedUrl(null);
    setRecordingDuration(0);
    setPlayingRecorded(false);
  };
  
  const useRecordedAudio = () => {
    if (recordedBlob) {
      // Determine the file extension based on the blob type
      let extension = '.webm'; // default fallback
      let fileName = `recorded-voice-${proverbId}-${Date.now()}`;
      
      if (recordedBlob.type.includes('mp4a.40.2') || recordedBlob.type.includes('aac')) {
        extension = '.m4a'; // AAC audio file
      } else if (recordedBlob.type.includes('mpeg')) {
        extension = '.mp3';
      } else if (recordedBlob.type.includes('mp4')) {
        extension = '.m4a'; // MP4 audio is typically M4A
      } else if (recordedBlob.type.includes('opus')) {
        extension = '.opus';
      } else if (recordedBlob.type.includes('webm')) {
        extension = '.webm';
      }
      
      // Create a File object from the recorded blob
      const recordedFile = new File(
        [recordedBlob], 
        fileName + extension, 
        { type: recordedBlob.type }
      );
      
      console.log('🎤 Using recorded audio as file:', {
        name: recordedFile.name,
        type: recordedFile.type,
        size: recordedFile.size,
        originalBlobType: recordedBlob.type
      });
      
      // Clean up any existing selected file URL
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      
      // Create a new blob URL specifically for the selected file
      const newAudioUrl = URL.createObjectURL(recordedFile);
      
      // Set the recorded file as selected file
      setSelectedFile(recordedFile);
      setAudioUrl(newAudioUrl); // Use new URL for preview
      setIsPlaying(false);
      
      // Clear recording states completely
      if (recordedUrl) {
        URL.revokeObjectURL(recordedUrl);
      }
      setRecordedBlob(null);
      setRecordedUrl(null);
      setRecordingDuration(0);
      setPlayingRecorded(false);
      
      showNotification('✅ Recorded audio ready for upload!', 'success');
    }
  };

  // Handle drag and drop
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = e.dataTransfer.files;
    handleFileSelect(files);
  };

  // Handle file input change
  const handleFileInputChange = (e) => {
    handleFileSelect(e.target.files);
  };

  // Play/stop audio preview for new file
  const togglePlayback = async () => {
    if (!audioRef.current || !selectedFile) {
      console.log('❌ Audio playback failed: No audio element or file');
      showNotification('❌ No audio file selected', 'error');
      return;
    }

    try {
      if (isPlaying) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        setIsPlaying(false);
        console.log('⏸️ Audio paused');
      } else {
        // Stop all other audio first
        stopAllAudio();
        
        console.log('▶️ Attempting to play selected file audio...');
        console.log('🎵 Selected file:', {
          name: selectedFile.name,
          type: selectedFile.type,
          size: selectedFile.size
        });
        console.log('🎵 Audio URL:', audioUrl);
        
        // Ensure we have a valid audioUrl
        if (!audioUrl) {
          console.log('🔧 No audioUrl found, creating new one from selected file');
          const newAudioUrl = URL.createObjectURL(selectedFile);
          setAudioUrl(newAudioUrl);
          audioRef.current.src = newAudioUrl;
        } else if (audioRef.current.src !== audioUrl) {
          console.log('🔧 Setting audio source to:', audioUrl);
          audioRef.current.src = audioUrl;
        }
        
        // Wait for audio to load if needed
        if (audioRef.current.readyState === 0) {
          console.log('📥 Audio not loaded, waiting...');
          await new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
              audioRef.current.removeEventListener('loadeddata', onLoadedData);
              audioRef.current.removeEventListener('error', onError);
              reject(new Error('Audio loading timeout'));
            }, 8000); // Increased timeout to 8 seconds
            
            const onLoadedData = () => {
              clearTimeout(timeout);
              audioRef.current.removeEventListener('loadeddata', onLoadedData);
              audioRef.current.removeEventListener('error', onError);
              console.log('✅ Audio loaded successfully');
              resolve();
            };
            
            const onError = (e) => {
              clearTimeout(timeout);
              audioRef.current.removeEventListener('loadeddata', onLoadedData);
              audioRef.current.removeEventListener('error', onError);
              console.error('❌ Audio loading failed:', e.target.error);
              reject(new Error(`Audio loading error: ${e.target.error?.message || 'Unknown error'}`));
            };
            
            audioRef.current.addEventListener('loadeddata', onLoadedData);
            audioRef.current.addEventListener('error', onError);
            
            // Try to load the audio
            console.log('🔄 Loading audio...');
            audioRef.current.load();
          });
        }
        
        console.log('▶️ Starting audio playback...');
        await audioRef.current.play();
        setIsPlaying(true);
        console.log('✅ Audio playing successfully');
      }
    } catch (error) {
      console.error('❌ Audio playback error:', error);
      setIsPlaying(false);
      
      if (error.name === 'NotSupportedError' || error.message.includes('Format error')) {
        showNotification('❌ Audio format not supported by browser. Try a different format.', 'error');
      } else if (error.name === 'NotAllowedError') {
        showNotification('❌ Audio playback blocked. Please allow audio in your browser', 'error');
      } else if (error.message.includes('loading timeout')) {
        showNotification('❌ Audio file took too long to load. Try a smaller file.', 'error');
      } else {
        showNotification(`❌ Audio playback failed: ${error.message}`, 'error');
      }
    }
  };

  // Play/stop existing voice file
  const toggleExistingPlayback = async () => {
    if (!existingAudioRef.current || !existingVoice?.fileUrl) {
      showNotification('❌ No existing voice file to play', 'error');
      return;
    }

    try {
      if (playingExisting) {
        existingAudioRef.current.pause();
        existingAudioRef.current.currentTime = 0;
        setPlayingExisting(false);
        console.log('⏸️ Existing audio paused');
      } else {
        // Stop all other audio first
        stopAllAudio();
        
        console.log('▶️ Playing existing voice file:', existingVoice.fileUrl);
        
        // Construct full URL for the audio file
        const API_BASE_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:3000';
        const audioUrl = `${API_BASE_URL}${existingVoice.fileUrl}`;
        console.log('🎵 Full audio URL:', audioUrl);
        
        // Set the source if it's different
        if (existingAudioRef.current.src !== audioUrl) {
          existingAudioRef.current.src = audioUrl;
        }
        
        // Wait for audio to load if needed
        if (existingAudioRef.current.readyState === 0) {
          console.log('📥 Existing audio not loaded, waiting...');
          await new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
              existingAudioRef.current.removeEventListener('loadeddata', onLoadedData);
              existingAudioRef.current.removeEventListener('error', onError);
              reject(new Error('Audio loading timeout'));
            }, 5000); // 5 second timeout
            
            const onLoadedData = () => {
              clearTimeout(timeout);
              existingAudioRef.current.removeEventListener('loadeddata', onLoadedData);
              existingAudioRef.current.removeEventListener('error', onError);
              resolve();
            };
            
            const onError = (e) => {
              clearTimeout(timeout);
              existingAudioRef.current.removeEventListener('loadeddata', onLoadedData);
              existingAudioRef.current.removeEventListener('error', onError);
              reject(new Error(`Audio loading error: ${e.target.error?.message || 'Unknown error'}`));
            };
            
            existingAudioRef.current.addEventListener('loadeddata', onLoadedData);
            existingAudioRef.current.addEventListener('error', onError);
            
            // Try to load the audio
            existingAudioRef.current.load();
          });
        }
        
        await existingAudioRef.current.play();
        setPlayingExisting(true);
        console.log('✅ Existing audio playing successfully');
        
        showNotification('🎵 Playing existing voice file', 'success');
      }
    } catch (error) {
      console.error('❌ Existing audio playback error:', error);
      setPlayingExisting(false);
      
      if (error.name === 'NotSupportedError') {
        showNotification('❌ Audio format not supported by browser', 'error');
      } else if (error.name === 'NotAllowedError') {
        showNotification('❌ Audio playback blocked. Please allow audio in your browser', 'error');
      } else {
        showNotification(`❌ Could not play existing voice: ${error.message}`, 'error');
      }
    }
  };

  // Upload voice file
  const handleUpload = async () => {
    if (!selectedFile || !proverbId) {
      showNotification('❌ Please select a file and ensure proverb ID is provided', 'error');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // Simulate upload progress (since fetch doesn't provide real progress)
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + Math.random() * 10;
        });
      }, 200);

      const result = await uploadVoiceFile(proverbId, selectedFile, language);
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      // Check if it was an update or create operation
      const isUpdate = result.isUpdate || existingVoice !== null;
      const notificationMessage = isUpdate 
        ? '✅ Voice file updated successfully!' 
        : '✅ Voice file uploaded successfully!';
      
      console.log(`🎵 Upload result:`, {
        isUpdate,
        voiceId: result.voice?.id,
        message: result.message
      });
      
      showNotification(notificationMessage, 'success');
      
      // Reset form
      setSelectedFile(null);
      setUploadProgress(0);
      setIsUpdating(false);
      
      // Clean up audio URLs
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
        setAudioUrl(null);
      }
      
      // Clean up recorded audio if it was used
      if (recordedUrl) {
        URL.revokeObjectURL(recordedUrl);
        setRecordedUrl(null);
      }
      setRecordedBlob(null);
      
      // Reload existing voice to show updated file
      try {
        const updatedVoice = await getVoiceByProverbId(proverbId);
        setExistingVoice(updatedVoice);
        console.log('🔄 Reloaded voice after upload:', updatedVoice);
        
        if (updatedVoice) {
          showNotification('🔄 Voice file information updated', 'info');
        }
      } catch (error) {
        console.error('Failed to reload updated voice:', error);
        // This is not critical, so don't show error notification
      }
      
      // Callback to parent
      if (onUploadSuccess) {
        onUploadSuccess({
          ...result,
          isUpdate: isUpdate
        });
      }
      
      // Close modal after a short delay to show the success message
      setTimeout(() => {
        onClose && onClose();
      }, 2000);
      
    } catch (error) {
      console.error('Upload error:', error);
      
      // Parse different types of upload errors
      let errorMessage = `❌ Upload failed: ${error.message}`;
      
      if (error.message.includes('Authentication')) {
        errorMessage = '❌ Authentication failed. Please log in again.';
      } else if (error.message.includes('Permission')) {
        errorMessage = '❌ Permission denied. Admin access required.';
      } else if (error.message.includes('File too large')) {
        errorMessage = '❌ File too large. Please select a smaller audio file.';
      } else if (error.message.includes('Invalid file format')) {
        errorMessage = '❌ Invalid file format. Please upload an MP3 or other supported audio file.';
      }
      
      showNotification(errorMessage, 'error');
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  // Clear selected file
  const clearFile = () => {
    // Clean up audio URL
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    
    setSelectedFile(null);
    setAudioUrl(null);
    setIsPlaying(false);
    setUploadProgress(0);
    
    // Reset the audio element
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.src = '';
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    console.log('🧹 File cleared and audio element reset');
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="voice-upload-overlay">
      <div className="voice-upload-modal">
        <div className="voice-upload-header">
          <h3>
            <FaMicrophone className="icon" />
            Upload Voice Recording
          </h3>
          <button className="close-btn" onClick={onClose} disabled={uploading}>
            <FaTimes />
          </button>
        </div>

        <div className="voice-upload-content">
          <div className="upload-info">
            <p><strong>Proverb ID:</strong> {proverbId}</p>
            <p><strong>Language:</strong> {language.charAt(0).toUpperCase() + language.slice(1)}</p>
          </div>

          {/* Existing Voice File */}
          {loadingExisting ? (
            <div className="existing-voice-loading">
              <div className="loading-spinner" />
              <span>Loading existing voice file...</span>
            </div>
          ) : existingVoice ? (
            <div className="existing-voice-section">
              <h4>Current Voice File:</h4>
              <div className="existing-voice-info">
                <div className="voice-details">
                  <div className="voice-name">{existingVoice.originalName || existingVoice.filename || 'voice-recording.mp3'}</div>
                  <div className="voice-meta">
                    <span>Language: {existingVoice.language || language}</span>
                    {existingVoice.fileSize && <span>Size: {formatFileSize(existingVoice.fileSize)}</span>}
                    {existingVoice.mimeType && <span>Type: {existingVoice.mimeType}</span>}
                    {existingVoice.created_at && (
                      <span>Uploaded: {new Date(existingVoice.created_at).toLocaleDateString()}</span>
                    )}
                  </div>
                  <div className="voice-file-url">
                    <small>File: {existingVoice.fileUrl}</small>
                  </div>
                </div>
                
                {/* Play existing voice button */}
                <div className="existing-voice-controls">
                  <button
                    className={`play-btn ${playingExisting ? 'playing' : ''}`}
                    onClick={toggleExistingPlayback}
                    type="button"
                    disabled={!existingVoice.fileUrl}
                  >
                    {playingExisting ? <FaStop /> : <FaPlay />}
                  </button>
                  <span className="audio-label">
                    {playingExisting ? 'Playing...' : 'Play Current'}
                  </span>
                </div>
              </div>
              
              {/* Hidden audio element for existing voice */}
              <audio
                ref={existingAudioRef}
                onEnded={() => {
                  setPlayingExisting(false);
                  console.log('🎵 Existing audio playback ended');
                }}
                onError={(e) => {
                  console.error('❌ Existing audio error:', e.target.error);
                  setPlayingExisting(false);
                  showNotification('❌ Could not load existing voice file', 'error');
                }}
                onLoadedData={() => {
                  console.log('🎵 Existing audio loaded successfully');
                }}
                onCanPlay={() => {
                  console.log('🎵 Existing audio ready to play');
                }}
                preload="metadata"
                crossOrigin="anonymous"
              />
              
              
            </div>
          ) : (
            <div className="no-existing-voice">
              <p>No voice file found for this proverb. Upload one below.</p>
            </div>
          )}

          {/* Voice Recording Section */}
          <div className="voice-recording-section">
            <h4>🎤 Record Voice</h4>
            <p className="recording-hint">Record your voice directly in the browser</p>
            
            {!recordedBlob && !isRecording && (
              <div className="recording-controls">
                <button
                  className="record-btn start-recording"
                  onClick={startRecording}
                  disabled={uploading || isRecording}
                >
                  <FaMicrophone className="record-icon" />
                  Start Recording
                </button>
                <p className="mic-hint">Click to start recording with your microphone</p>
              </div>
            )}
            
            {isRecording && (
              <div className="recording-active">
                <div className="recording-indicator">
                  <div className="recording-dot"></div>
                  <span className="recording-text">Recording...</span>
                </div>
                <div className="recording-duration">
                  Duration: {Math.floor(recordingDuration / 60)}:{String(recordingDuration % 60).padStart(2, '0')}
                </div>
                <button
                  className="record-btn stop-recording"
                  onClick={stopRecording}
                >
                  <FaStop className="record-icon" />
                  Stop Recording
                </button>
              </div>
            )}
            
            {recordedBlob && recordedUrl && (
              <div className="recorded-audio-preview">
                <div className="recorded-info">
                  <div className="recorded-details">
                    <div className="recorded-name">🎤 Recorded Audio</div>
                    <div className="recorded-meta">
                      <span>Duration: {Math.floor(recordingDuration / 60)}:{String(recordingDuration % 60).padStart(2, '0')}</span>
                      <span>Size: {formatFileSize(recordedBlob.size)}</span>
                      <span>Type: {recordedBlob.type}</span>
                    </div>
                  </div>
                  
                  <div className="recorded-controls">
                    <button
                      className={`play-btn ${playingRecorded ? 'playing' : ''}`}
                      onClick={playRecordedAudio}
                      type="button"
                    >
                      {playingRecorded ? <FaStop /> : <FaPlay />}
                    </button>
                    <span className="audio-label">
                      {playingRecorded ? 'Playing...' : 'Preview'}
                    </span>
                  </div>
                  
                  <div className="recorded-actions">
                    <button
                      className="use-recording-btn"
                      onClick={useRecordedAudio}
                      disabled={uploading}
                    >
                      <FaCheck />
                      Use This Recording
                    </button>
                    <button
                      className="clear-recording-btn"
                      onClick={clearRecording}
                      disabled={uploading}
                    >
                      <FaTimes />
                      Discard
                    </button>
                  </div>
                </div>
                
                {/* Hidden audio element for recorded audio preview */}
                <audio
                  ref={recordedAudioRef}
                  src={recordedUrl}
                  onEnded={() => {
                    setPlayingRecorded(false);
                    console.log('🎤 Recorded audio playback ended');
                  }}
                  onError={(e) => {
                    console.error('❌ Recorded audio error:', e.target.error);
                    setPlayingRecorded(false);
                    showNotification('❌ Could not play recorded audio', 'error');
                  }}
                  onLoadedData={() => {
                    console.log('🎤 Recorded audio loaded successfully');
                  }}
                  onCanPlay={() => {
                    console.log('🎤 Recorded audio ready to play');
                  }}
                  onLoadStart={() => {
                    console.log('🎤 Recorded audio started loading');
                  }}
                  preload="metadata"
                  controls={false}
                />
              </div>
            )}
            
            <div className="recording-divider">
              <span>OR</span>
            </div>
          </div>

          {/* File Drop Zone */}
          <div
            className={`file-drop-zone ${dragActive ? 'drag-active' : ''} ${selectedFile ? 'has-file' : ''}`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => !selectedFile && fileInputRef.current?.click()}
          >
            {!selectedFile ? (
              <>
                <FaUpload className="upload-icon" />
                <p className="upload-text">
                  Drag and drop your audio file here, or <span className="click-text">click to browse</span>
                </p>
                <p className="upload-hint">
                  Supported formats: MP3, WAV, OGG (Max 10MB)
                </p>
              </>
            ) : (
              <div className="file-info">
                <div className="file-details">
                  <div className="file-name">{selectedFile.name}</div>
                  <div className="file-meta">
                    <span>Size: {formatFileSize(selectedFile.size)}</span>
                    <span>Type: {selectedFile.type}</span>
                  </div>
                </div>
                
                {/* Audio Preview Controls */}
                <div className="audio-controls">
                  <button
                    className={`play-btn ${isPlaying ? 'playing' : ''}`}
                    onClick={togglePlayback}
                    type="button"
                  >
                    {isPlaying ? <FaStop /> : <FaPlay />}
                  </button>
                  <span className="audio-label">Preview</span>
                </div>

                <button className="clear-file-btn" onClick={clearFile} type="button">
                  <FaTimes />
                </button>
              </div>
            )}
          </div>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/*,.mp3,.wav,.ogg"
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
            disabled={uploading}
          />

          {/* Audio element for preview */}
          {selectedFile && audioUrl && (
            <audio
              ref={audioRef}
              src={audioUrl}
              onEnded={() => {
                setIsPlaying(false);
                console.log('🎵 Selected file audio playback ended');
              }}
              onError={(e) => {
                console.error('❌ Selected file audio element error:', {
                  error: e.target.error,
                  code: e.target.error?.code,
                  message: e.target.error?.message,
                  networkState: e.target.networkState,
                  readyState: e.target.readyState,
                  src: e.target.src,
                  selectedFileType: selectedFile?.type,
                  selectedFileName: selectedFile?.name
                });
                setIsPlaying(false);
                
                // Don't show notification here as it will be handled by the play function
                // showNotification('❌ Audio file could not be loaded', 'error');
              }}
              onLoadStart={() => {
                console.log('🎵 Selected file audio started loading');
              }}
              onLoadedData={() => {
                console.log('🎵 Selected file audio loaded successfully');
              }}
              onLoadedMetadata={(e) => {
                const duration = e.target.duration;
                if (!isNaN(duration)) {
                  console.log(`🎵 Audio loaded: ${duration.toFixed(1)}s duration`);
                }
              }}
              onCanPlay={() => {
                console.log('🎵 Selected file audio ready to play');
              }}
              preload="metadata"
              crossOrigin="anonymous"
            />
          )}

          {/* Upload Progress */}
          {uploading && (
            <div className="upload-progress">
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <div className="progress-text">
                Uploading... {Math.round(uploadProgress)}%
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="upload-actions">
            <button
              className="cancel-btn"
              onClick={onClose}
              disabled={uploading}
            >
              Cancel
            </button>
            <button
              className="upload-btn"
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
            >
              {uploading ? (
                <>
                  <div className="loading-spinner" />
                  {existingVoice ? 'Updating...' : 'Uploading...'}
                </>
              ) : (
                <>
                  <FaCheck />
                  {existingVoice ? 'Update Voice' : 'Upload Voice'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoiceUpload;
